import React, { useEffect, useState } from "react";

// Examples of what is worth bleeping, drawn from the sector list in
// CategoryStrip. Kept to 15 characters or fewer so a pill always fits the
// gutter at the 1280px breakpoint without ellipsing: the widest pill there is
// 28px padding + 12px icon + 8px gap + 15 chars at ~6.4px = 146px, against
// 162px of usable rail. Anything longer would ellipse on the narrowest screen
// that still shows the rails.
//
// The count is free. Order comes off a shuffled deck rather than a walk along
// this array, so a slot can no longer parity-lock onto a subset and the list
// length no longer has to be coprime with anything. Add and remove freely.
//
// Icons are Font Awesome 6 Free solid, already loaded from the CDN in
// public/index.html (6.5.1). A few are reused across two labels -- shirts and
// jeans, sneakers and boots -- because FA Free has no trousers or boot glyph,
// and at 12px in a bubble that shows one label at a time it does not read as a
// repeat. The category PNGs are deliberately not used here: they run 206-419KB
// each, which is indefensible for a 12px decoration.
const LABELS = [
  // Health and personal care
  { icon: "fa-solid fa-pills", text: "Fake medicine" },
  { icon: "fa-solid fa-spray-can-sparkles", text: "Fake makeup" },
  { icon: "fa-solid fa-soap", text: "Fake soap" },
  { icon: "fa-solid fa-pump-soap", text: "Fake detergent" },

  // Food, drink and groceries
  { icon: "fa-solid fa-baby", text: "Fake baby food" },
  { icon: "fa-solid fa-cookie-bite", text: "Fake chocolate" },
  { icon: "fa-solid fa-cookie", text: "Fake snacks" },
  { icon: "fa-solid fa-bottle-droplet", text: "Fake oil" },
  { icon: "fa-solid fa-cube", text: "Fake sugar" },
  { icon: "fa-solid fa-bowl-rice", text: "Fake rice" },
  { icon: "fa-solid fa-bread-slice", text: "Fake flour" },
  { icon: "fa-solid fa-jar", text: "Fake honey" },
  { icon: "fa-solid fa-cow", text: "Fake milk" },
  { icon: "fa-solid fa-mug-hot", text: "Fake coffee" },
  { icon: "fa-solid fa-mug-saucer", text: "Fake tea" },
  { icon: "fa-solid fa-wine-bottle", text: "Bootleg whisky" },
  { icon: "fa-solid fa-wine-glass", text: "Fake wine" },
  { icon: "fa-solid fa-glass-water", text: "Fake juice" },
  { icon: "fa-solid fa-bottle-water", text: "Fake soda" },
  { icon: "fa-solid fa-martini-glass-citrus", text: "Fake drinks" },

  // Agriculture
  { icon: "fa-solid fa-seedling", text: "Fake fertiliser" },
  { icon: "fa-solid fa-bug", text: "Fake pesticide" },
  { icon: "fa-solid fa-wheat-awn", text: "Fake seeds" },

  // Fuel
  { icon: "fa-solid fa-gas-pump", text: "Fake petrol" },
  { icon: "fa-solid fa-oil-can", text: "Fake diesel" },
  { icon: "fa-solid fa-fire-flame-simple", text: "Fake paraffin" },
  { icon: "fa-solid fa-fire", text: "Fake kerosene" },

  // Electronics and household
  { icon: "fa-solid fa-mobile-screen", text: "Fake phone" },
  { icon: "fa-solid fa-laptop", text: "Fake laptops" },
  { icon: "fa-solid fa-tablet-screen-button", text: "Fake tablets" },
  { icon: "fa-solid fa-tv", text: "Fake TVs" },
  { icon: "fa-solid fa-plug", text: "Fake charger" },
  { icon: "fa-solid fa-battery-half", text: "Fake batteries" },
  { icon: "fa-solid fa-lightbulb", text: "Fake bulbs" },
  { icon: "fa-solid fa-clock", text: "Fake watches" },
  { icon: "fa-solid fa-puzzle-piece", text: "Fake toys" },

  // Motoring
  { icon: "fa-solid fa-car-side", text: "Fake car parts" },
  { icon: "fa-solid fa-record-vinyl", text: "Fake tyres" },

  // Apparel
  { icon: "fa-solid fa-shoe-prints", text: "Fake sneakers" },
  { icon: "fa-solid fa-shoe-prints", text: "Fake boots" },
  { icon: "fa-solid fa-shirt", text: "Fake shirts" },
  { icon: "fa-solid fa-shirt", text: "Fake jeans" },
  { icon: "fa-solid fa-person-dress", text: "Fake dresses" },
  { icon: "fa-solid fa-vest", text: "Fake jackets" },
  { icon: "fa-solid fa-bag-shopping", text: "Fake bags" },

  // Building and finish
  { icon: "fa-solid fa-trowel-bricks", text: "Fake cement" },
  { icon: "fa-solid fa-fill-drip", text: "Fake paint" },

  // Documents and money
  { icon: "fa-solid fa-money-bill-1", text: "Fake banknotes" },
  { icon: "fa-solid fa-building-columns", text: "Fake bank app" },
  { icon: "fa-solid fa-id-card", text: "Fake ID card" },
  { icon: "fa-solid fa-graduation-cap", text: "Fake diploma" },
  { icon: "fa-solid fa-certificate", text: "Fake licence" },
  { icon: "fa-solid fa-scroll", text: "Fake land title" },
];

const range = (n) => Array.from({ length: n }, (_, i) => i);

// Fisher-Yates, returning a new array so callers can treat it as a value.
const shuffle = (arr) => {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

/**
 * A fresh pass over every label, with the ones currently on screen held back to
 * the end.
 *
 * Shuffling the whole list outright would satisfy "no repeats within a pass"
 * and still let a label reappear the moment the deck turns over, in a different
 * slot, while the old bubble is still fading out beside it. Dealing the unseen
 * labels first and the visible ones last means each has been replaced before it
 * can come round again.
 */
const buildDeck = (total, visible) => {
  const held = new Set(visible);
  const fresh = range(total).filter((i) => !held.has(i));
  return [...shuffle(fresh), ...shuffle([...held])];
};

// Three layouts. The first two are switched by CSS at 1280px and never both
// visible; the third is for a different container entirely.
//
//   rails  - three bubbles down each side gutter, wide screens only
//   inline - two bubbles in the normal flow between the subhead and the CTA,
//            for the narrow screens where there is no gutter left to use
//   column - one stack filling a tall narrow panel, alternating sides so it
//            reads as an exchange. Used by the sign-in page's right half, where
//            there is no centred copy column for rails to sit beside
//
// `lives` are deliberately non-harmonic, so swaps never settle into a metronome
// and a resize past the breakpoint and back cannot leave the bubbles pulsing in
// lockstep. Which label a slot shows is drawn from a shuffled deck, so no
// relationship between the list length and the slot count is required.
//
// `leads` are first-run head starts as a fraction of each slot's own lifetime:
// a slot entering at 0.67 mounts two thirds of the way through, so the set
// looks populated immediately instead of filling in one bubble at a time.
// Fractions rather than seconds so they stay valid whatever `lives` becomes --
// an absolute lead longer than its own duration would land past the end of the
// animation, where animationend never fires and that slot would stall.
//
// `sizes` and `tails` are fixed per slot so geometry stays stable as labels
// cycle. The inline pair takes one of each tail direction, which reads as a
// short exchange rather than two loose tags.
const VARIANTS = {
  rails: {
    lives: [7.1, 8.3, 9.7, 7.7, 10.4, 8.9],
    sizes: ["lg", "sm", "md", "md", "lg", "sm"],
    leads: [0, 0.17, 0.34, 0.5, 0.67, 0.84],
    tails: ["l", "l", "l", "r", "r", "r"],
  },
  inline: {
    lives: [6.4, 7.9],
    sizes: ["md", "sm"],
    leads: [0, 0.5],
    tails: ["l", "r"],
  },
  column: {
    lives: [7.4, 9.1, 8.2, 10.3, 7.9],
    sizes: ["md", "lg", "sm", "md", "lg"],
    leads: [0, 0.2, 0.4, 0.6, 0.8],
    tails: ["l", "r", "l", "r", "l"],
  },
  // In-flow stack for a content column. One size throughout, unlike the
  // decorative variants: these are usually clickable, and a bubble's size would
  // otherwise read as a ranking of the suggestions rather than as depth.
  chat: {
    lives: [6.7, 8.3, 7.3, 9.1],
    sizes: ["md", "md", "md", "md"],
    leads: [0, 0.25, 0.5, 0.75],
    tails: ["l", "r", "l", "r"],
  },
};

/**
 * Speech bubbles. `variant` picks the layout: "rails" for the side gutters on
 * wide screens, "inline" for the compressed row that replaces them on narrow
 * ones, "column" for a tall narrow panel, "chat" for an in-flow stack in a
 * content column. On the hero, mount rails and inline together; CSS shows
 * exactly one.
 *
 * `labels` overrides the counterfeit examples with any `{ icon, text }` list of
 * any length. Order is a shuffled deck dealt per mount: every label appears once
 * per pass, the pass is re-dealt when it runs out, and two pages showing the
 * component never open on the same sequence.
 *
 * Passing `onSelect` makes each bubble a real button that hands back its text,
 * which is the difference between showing examples and offering them. The
 * decorative default stays `aria-hidden` and `pointer-events: none`; the
 * interactive form is announced, focusable and pauses the whole set on hover or
 * focus, because a target that fades out from under a reaching cursor is worse
 * than no target.
 *
 * There are no timers. Each bubble runs one pass of the bl-bubble-life
 * keyframes; onAnimationEnd swaps that slot's label, the key changes, React
 * remounts the element and the animation restarts. The stylesheet therefore
 * controls the cadence, the 1280px breakpoint, the reduced-motion opt-out and
 * the background-tab pause, and none of them need a guard here. The hidden
 * variant is display:none, so its animations never run and it costs nothing.
 */
const HeroBubbles = ({ variant = "rails", labels = LABELS, onSelect }) => {
  const { lives, sizes, leads, tails } = VARIANTS[variant];
  const slotCount = lives.length;
  const interactive = typeof onSelect === "function";
  const labelCount = labels.length;

  // Order is a shuffled deck rather than a walk along the list. The component
  // appears on the home hero, the about band, /signin and the post page, so an
  // in-order walk meant the same six pills in the same sequence on every one of
  // them, every visit. Dealing a fresh permutation per mount fixes that without
  // giving up the no-repeat guarantee: a deck holds each label exactly once, so
  // nothing can recur until the whole list has been through.
  const [cycle, setCycle] = useState(() => {
    const deck = shuffle(range(labelCount));
    return {
      // labelCount can in principle be below slotCount, in which case the
      // modulo repeats rather than handing renderSlot an undefined.
      labels: Array.from(
        { length: slotCount },
        (_, i) => deck[i % Math.max(deck.length, 1)] ?? 0,
      ),
      gens: Array(slotCount).fill(0), // bumped per swap; drives the React key
      deck: deck.slice(slotCount),
    };
  });

  const [paused, setPaused] = useState(false);

  // Freezing the animation also freezes the animationend that drives the swap,
  // so a hidden tab does no work and resumes on the exact frame it left.
  useEffect(() => {
    const onVisibility = () => setPaused(document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  // Draws the next label off the deck, re-dealing when it runs out. The updater
  // must not close over `labels` under that name: `c.labels` is one entry per
  // slot while the prop is the whole list, and an earlier version named its
  // local copy `labels`, shadowed the prop, and bounded the sequence at the slot
  // count. That is why the hero looked like it only knew six items.
  //
  // Calling shuffle() in here makes the updater impure, which is deliberate and
  // harmless: StrictMode invokes it twice and keeps the second result, and both
  // results are valid deals. A ref would not be, which is why the draw stays in
  // the updater.
  const advance = (slot) =>
    setCycle((c) => {
      const deck = c.deck.length ? c.deck : buildDeck(labelCount, c.labels);
      const nextLabels = c.labels.slice();
      nextLabels[slot] = deck[0] ?? 0;
      const gens = c.gens.slice();
      gens[slot] += 1;
      return { labels: nextLabels, gens, deck: deck.slice(1) };
    });

  const renderSlot = (slot) => {
    const label = labels[cycle.labels[slot]];
    // A button when it does something, a span when it is scenery. Rendering a
    // button either way would put every decorative pill in the tab order.
    const Bubble = interactive ? "button" : "span";
    return (
      <span
        className="home-bubble-slot"
        key={slot}
      >
        <Bubble
          key={`${slot}-${cycle.gens[slot]}`}
          {...(interactive
            ? { type: "button", onClick: () => onSelect(label.text) }
            : {})}
          className={`home-bubble home-bubble--${sizes[slot]} home-bubble--tail-${tails[slot]}${
            interactive ? " home-bubble--action" : ""
          }`}
          style={{
            animationDuration: `${lives[slot]}s`,
            animationDelay:
              cycle.gens[slot] === 0
                ? `-${(lives[slot] * leads[slot]).toFixed(2)}s`
                : "0s",
          }}
          onAnimationEnd={(e) => {
            if (e.target !== e.currentTarget) return;
            // ignore the rail's own fade-in
            if (e.animationName !== "bl-bubble-life") return;
            advance(slot);
          }}
        >
          <i
            className={label.icon}
            aria-hidden="true"
          />
          {/* the clip lives here rather than on the bubble, which must not
              hide its overflow or it would cut off the tail */}
          <span className="home-bubble__text">{label.text}</span>
        </Bubble>
      </span>
    );
  };

  const groupClass = (name) =>
    `home-bubbles home-bubbles--${name}${paused ? " home-bubbles--paused" : ""}`;

  if (variant === "inline" || variant === "column" || variant === "chat") {
    return (
      <div
        className={groupClass(variant)}
        aria-hidden={interactive ? undefined : "true"}
      >
        {lives.map((_, slot) => renderSlot(slot))}
      </div>
    );
  }

  return (
    <>
      <div
        className={groupClass("left")}
        aria-hidden="true"
      >
        {[0, 1, 2].map(renderSlot)}
      </div>
      <div
        className={groupClass("right")}
        aria-hidden="true"
      >
        {[3, 4, 5].map(renderSlot)}
      </div>
    </>
  );
};

export default HeroBubbles;
